/**
 * Cookiebot consent adapter.
 *
 * Cookiebot owns consent policy, geography, UI, persistence, and Google
 * Consent Mode updates. ADU Lab remains in manual blocking mode: the existing
 * tag controllers below are the only code allowed to load or dispatch vendor
 * tags. Until Cookiebot reports a ready state, non-essential tags stay idle
 * and consented browser events wait in-memory.
 */

import { loadMetaPixel, cleanupMetaPixel } from '../consent/metaPixelTag.js';
import {
  loadGa4,
  cleanupGa4,
  loadGoogleAdsTag,
  cleanupGoogleAdsTag,
  loadMicrosoftUetTag,
  cleanupMicrosoftUetTag,
  loadRedditPixelTag,
  cleanupRedditPixelTag,
} from '../consent/ga4Tag.js';
import { loadHotjar, cleanupHotjar } from '../consent/hotjarTag.js';

const COOKIEBOT_SCRIPT_ID = 'Cookiebot';
const COOKIEBOT_SCRIPT_SRC = 'https://consent.cookiebot.com/uc.js';
const COOKIEBOT_EVENTS = Object.freeze([
  'CookiebotOnConsentReady',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
]);
const COOKIEBOT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMPTY_COOKIEBOT_ID = '00000000-0000-0000-0000-000000000000';

const LEGACY_CONSENT_COOKIE = 'cc_cookie';
const LEGACY_STORAGE_KEYS = Object.freeze([
  LEGACY_CONSENT_COOKIE,
  'cc_do_not_sell_user_pref',
  'cc_gpc_signaled',
  'cc_anon_id',
  'cc_last_record_id',
]);
const LEGACY_CATEGORIES = Object.freeze([
  'necessary',
  'analytics',
  'functionality',
  'marketing',
]);
const MAX_PENDING_CONSENT_TASKS = 100;
// Readiness budget for callers that snapshot consent at a user action. Cookiebot
// normally resolves an order of magnitude faster; this is the ceiling past which
// a buyer waits on nothing.
export const CONSENT_SETTLE_TIMEOUT_MS = 1500;
// How long uc.js may stay loaded-but-silent before we call it a broken install.
// Cookiebot answers within a second in practice; the only thing that sits here
// indefinitely is a domain group that does not list this host.
export const COOKIEBOT_READY_WATCHDOG_MS = 15000;

const listeners = new Set();
const pendingConsentTasks = [];

let initialized = false;
let configured = false;
let providerUnavailable = false;
let readinessWatchdogTimer = null;
let legacyMigrationAttempted = false;
let legacyMigrationFailureReported = false;
let legacyDenials = null;
let readyResolver;
let readyPromise = new Promise((resolve) => {
  readyResolver = resolve;
});

let consentState = Object.freeze({
  ready: false,
  providerConfigured: false,
  necessary: true,
  analytics: false,
  functionality: false,
  marketing: false,
  doNotSell: false,
  gpc: false,
});

function readViteEnv(name) {
  try {
    const value = import.meta?.env?.[name];
    return typeof value === 'string' ? value.trim() : '';
  } catch {
    return '';
  }
}

function normalizeDomainGroupId(value) {
  const normalized = String(value || '').trim();
  if (
    !COOKIEBOT_ID_PATTERN.test(normalized) ||
    normalized.toLowerCase() === EMPTY_COOKIEBOT_ID
  ) {
    return '';
  }
  return normalized;
}

function nativeGpcIsActive() {
  try {
    return navigator.globalPrivacyControl === true;
  } catch {
    return false;
  }
}

function safeStorageGet(key) {
  try {
    return window.localStorage?.getItem?.(key) || null;
  } catch {
    return null;
  }
}

function safeStorageRemove(key) {
  try {
    window.localStorage?.removeItem?.(key);
  } catch {
    /* Browser privacy settings may make storage unavailable. */
  }
}

function parseLegacyCookieValue(rawValue) {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(rawValue));
    const categories = parsed?.categories;
    if (!Array.isArray(categories)) return null;
    const categorySet = new Set(categories);
    if (
      categorySet.size !== categories.length ||
      !categorySet.has('necessary') ||
      categories.some((category) => !LEGACY_CATEGORIES.includes(category))
    ) {
      return null;
    }
    return categories;
  } catch {
    return null;
  }
}

function readLegacyCookieCategories() {
  const fromStorage = parseLegacyCookieValue(safeStorageGet(LEGACY_CONSENT_COOKIE));
  if (fromStorage) return fromStorage;

  try {
    const match = String(document.cookie || '')
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${LEGACY_CONSENT_COOKIE}=`));
    return parseLegacyCookieValue(match?.slice(LEGACY_CONSENT_COOKIE.length + 1));
  } catch {
    return null;
  }
}

function readLegacyDenials() {
  const categories = readLegacyCookieCategories();
  const categorySet = categories ? new Set(categories) : null;
  const legacyDoNotSell = safeStorageGet('cc_do_not_sell_user_pref') === '1';
  const legacyGpc = safeStorageGet('cc_gpc_signaled') === '1';
  const currentGpc = nativeGpcIsActive();
  const gpc = legacyGpc || currentGpc;
  const rejectedAll = !!categorySet && categorySet.size === 1;

  const denials = {
    analytics: gpc || (!!categorySet && !categorySet.has('analytics')),
    functionality: !!categorySet && !categorySet.has('functionality'),
    marketing:
      gpc ||
      legacyDoNotSell ||
      (!!categorySet && !categorySet.has('marketing')),
    doNotSell: legacyDoNotSell || legacyGpc || rejectedAll,
    gpc,
    hasLegacyPreference:
      legacyDoNotSell || legacyGpc || !!categorySet,
  };

  denials.any = denials.analytics || denials.functionality || denials.marketing;
  return denials;
}

function expireLegacyCookie() {
  try {
    const host = window.location?.hostname || '';
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=/';
    document.cookie = `${LEGACY_CONSENT_COOKIE}=; ${expiry}`;
    if (host) {
      document.cookie = `${LEGACY_CONSENT_COOKIE}=; ${expiry}; domain=${host}`;
      if (!host.startsWith('.')) {
        document.cookie = `${LEGACY_CONSENT_COOKIE}=; ${expiry}; domain=.${host}`;
      }
    }
  } catch {
    /* Cookie deletion is best-effort. */
  }
}

function clearLegacyStorage() {
  LEGACY_STORAGE_KEYS.forEach(safeStorageRemove);
  expireLegacyCookie();
  try {
    delete window.__cc_consent;
    delete window.__gpc_handled;
  } catch {
    /* SSR or a non-configurable legacy property. */
  }
}

function clearNonPreferenceLegacyStorage() {
  ['cc_anon_id', 'cc_last_record_id'].forEach(safeStorageRemove);
  try {
    delete window.__cc_consent;
    delete window.__gpc_handled;
  } catch {
    /* SSR or a non-configurable legacy property. */
  }
}

function applyTrackingState(state) {
  const allowMarketing = state.ready && state.marketing && !state.doNotSell && !state.gpc;
  const allowAnalytics = state.ready && state.analytics && !state.gpc;

  if (allowMarketing) {
    loadMetaPixel();
    loadGoogleAdsTag();
    loadMicrosoftUetTag();
    loadRedditPixelTag();
  } else {
    cleanupMetaPixel();
    cleanupGoogleAdsTag();
    cleanupMicrosoftUetTag();
    cleanupRedditPixelTag();
  }

  if (allowAnalytics) {
    loadGa4();
    loadHotjar();
  } else {
    cleanupGa4();
    cleanupHotjar();
  }
}

function categoryAllowed(category, state = consentState) {
  if (category === 'necessary') return true;
  if (!state.ready) return false;
  if (category === 'analytics') return state.analytics && !state.gpc;
  if (category === 'functionality') return state.functionality;
  if (category === 'marketing') {
    return state.marketing && !state.doNotSell && !state.gpc;
  }
  return false;
}

function taskCategoriesAllowed(categories, state = consentState) {
  const required = Array.isArray(categories) ? categories : [categories];
  return required.every((category) => categoryAllowed(category, state));
}

function flushPendingConsentTasks() {
  if (!consentState.ready) return;
  const pending = pendingConsentTasks.splice(0);
  pending.forEach(({ categories, callback }) => {
    if (!taskCategoriesAllowed(categories)) return;
    try {
      callback();
    } catch {
      /* Measurement failures never interrupt application behavior. */
    }
  });
}

function notifyListeners() {
  listeners.forEach((listener) => {
    try {
      listener(getConsentSnapshot());
    } catch {
      /* A subscriber cannot block the remaining consent lifecycle. */
    }
  });
}

function clearReadinessWatchdog() {
  if (readinessWatchdogTimer === null) return;
  clearTimeout(readinessWatchdogTimer);
  readinessWatchdogTimer = null;
}

function providerDialogIsPresent() {
  try {
    if (window.Cookiebot?.dialog) return true;
    return !!document.getElementById?.('CybotCookiebotDialog');
  } catch {
    return false;
  }
}

/**
 * uc.js loading is not the same as Cookiebot working. When the requesting host
 * is not in the domain group, Cookiebot answers with a tiny inert stub that only
 * console.warns — no CookiebotOn* event ever fires, the adapter stays pending
 * forever, and every tag stays silently dark. Say so loudly instead, and record
 * the outage so conversion metadata reports a broken provider rather than a
 * pending one that is still expected to answer.
 */
function startReadinessWatchdog(domainGroupId) {
  clearReadinessWatchdog();
  readinessWatchdogTimer = setTimeout(() => {
    readinessWatchdogTimer = null;
    if (consentState.ready) return;

    const provider = window.Cookiebot;
    // A resolved consent method, a recorded response, or a dialog on screen all
    // mean Cookiebot is alive — it is waiting on the user, not broken.
    if (provider?.consent?.method != null) return;
    if (provider?.hasResponse === true) return;
    if (providerDialogIsPresent()) return;

    let host = '';
    try {
      host = window.location?.hostname || '';
    } catch {
      /* SSR or a locked-down location object. */
    }

    markProviderUnavailable();
    console.error(
      `[cookiebot] No consent state ${COOKIEBOT_READY_WATCHDOG_MS}ms after uc.js loaded ` +
      `(host "${host}", domain group ${domainGroupId}). Cookiebot serves an inert stub when ` +
      'the requesting host is not in the domain group, so verify that this host is listed. ' +
      'Analytics and marketing tags remain disabled; this build is not ready for staging or production.'
    );
  }, COOKIEBOT_READY_WATCHDOG_MS);
}

function publishState(nextState) {
  const wasReady = consentState.ready;
  consentState = Object.freeze(nextState);
  applyTrackingState(consentState);
  notifyListeners();
  if (!wasReady && consentState.ready) {
    clearReadinessWatchdog();
    readyResolver?.(getConsentSnapshot());
  }
  flushPendingConsentTasks();
}

/**
 * Record that Cookiebot can no longer be expected to resolve on this page load.
 * Tracking stays blocked either way; the flag exists so downstream conversion
 * metadata reports a provider outage rather than an opt-out the user never made.
 */
function markProviderUnavailable() {
  clearReadinessWatchdog();
  if (providerUnavailable) return;
  providerUnavailable = true;
  notifyListeners();
}

function providerConsentState(provider, denials = null) {
  const consent = provider?.consent || {};
  const gpc = nativeGpcIsActive() || !!denials?.gpc;
  const analytics = !!consent.statistics && !gpc && !denials?.analytics;
  const functionality = !!consent.preferences && !denials?.functionality;
  const marketing = !!consent.marketing && !gpc && !denials?.marketing;
  const explicitMarketingOptOut =
    provider?.declined === true ||
    (provider?.hasResponse === true && !marketing);

  return {
    ready: true,
    providerConfigured: true,
    necessary: true,
    analytics,
    functionality,
    marketing,
    doNotSell: gpc || !!denials?.doNotSell || explicitMarketingOptOut,
    gpc,
  };
}

function legacyDenialsReflected(provider, denials) {
  const consent = provider?.consent || {};
  return (
    (!denials.analytics || consent.statistics === false) &&
    (!denials.functionality || consent.preferences === false) &&
    (!denials.marketing || consent.marketing === false)
  );
}

/**
 * The migration is deliberately fail-closed: until Cookiebot reflects a carried
 * opt-out, the adapter stays pending and every tag stays dark. That is the right
 * behavior but a silent one, so a migration that cannot complete says so once —
 * and never retries in a loop.
 */
function reportLegacyMigrationFailure(reason) {
  if (legacyMigrationFailureReported) return;
  legacyMigrationFailureReported = true;
  console.error(
    `[cookiebot] A prior consent denial could not be migrated to Cookiebot (${reason}). ` +
    'Consent stays unresolved and analytics and marketing tags remain disabled rather ' +
    'than letting provider state override an opt-out the user already made.'
  );
}

function submitLegacyDenials(provider, denials) {
  if (!denials.any || legacyDenialsReflected(provider, denials)) return true;
  if (typeof provider?.submitCustomConsent !== 'function') {
    reportLegacyMigrationFailure('Cookiebot.submitCustomConsent is unavailable');
    return false;
  }
  if (legacyMigrationAttempted) {
    reportLegacyMigrationFailure('Cookiebot did not reflect the submitted denial');
    return false;
  }

  legacyMigrationAttempted = true;
  const consent = provider.consent || {};
  provider.submitCustomConsent(
    !!consent.preferences && !denials.functionality,
    !!consent.statistics && !denials.analytics,
    !!consent.marketing && !denials.marketing,
    // isImpliedConsent. Nobody clicked anything here: this is a pre-existing
    // opt-out being carried across the provider boundary. Omitting the flag made
    // Cookiebot record it as an explicit 'strict' user choice it never received.
    true
  );

  return legacyDenialsReflected(provider, denials);
}

function handleCookiebotReady() {
  const provider = window.Cookiebot;
  if (!provider?.consent) return;

  const denials = legacyDenials || readLegacyDenials();
  legacyDenials = denials;

  if (denials.any && !submitLegacyDenials(provider, denials)) {
    // Keep the adapter pending while Cookiebot persists the migrated choice.
    // This prevents even an analytics-only tag from observing Cookiebot's
    // pre-migration state during the transition.
    return;
  }

  const nextState = providerConsentState(provider, denials.any ? denials : null);
  if (denials.hasLegacyPreference) clearLegacyStorage();
  legacyDenials = readLegacyDenials();
  publishState(nextState);
}

function attachCookiebotListeners() {
  COOKIEBOT_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleCookiebotReady, false);
  });
}

function injectCookiebotScript(domainGroupId) {
  const existing = document.getElementById(COOKIEBOT_SCRIPT_ID);
  if (existing) return existing;

  const script = document.createElement('script');
  script.id = COOKIEBOT_SCRIPT_ID;
  script.src = COOKIEBOT_SCRIPT_SRC;
  script.async = true;
  script.type = 'text/javascript';
  script.setAttribute('data-cbid', domainGroupId);
  // Official Cookiebot manual-blocking value. App controllers own every tag.
  script.setAttribute('data-blockingmode', 'none');
  script.setAttribute('data-cookieconsent', 'ignore');
  // Cookiebot owns the visible browser-consent surface. Keep its native Privacy
  // Trigger available on every route without recreating a first-party footer,
  // settings, or Do Not Sell control.
  script.setAttribute('data-widget-enabled', 'true');
  script.setAttribute('data-widget-position', 'top-right');
  script.onload = () => startReadinessWatchdog(domainGroupId);
  script.onerror = () => {
    markProviderUnavailable();
    console.error(
      '[cookiebot] CMP script failed to load; analytics and marketing tags remain disabled.'
    );
  };
  document.head.appendChild(script);
  return script;
}

/** Initialize Cookiebot once. Missing/invalid configuration fails closed. */
export function initializeCookiebot({ domainGroupId } = {}) {
  if (initialized) return configured;
  initialized = true;

  clearNonPreferenceLegacyStorage();
  legacyDenials = readLegacyDenials();
  if (legacyDenials.hasLegacyPreference && !legacyDenials.any) {
    // Prior grants are deliberately not migrated. Only denials may cross the
    // provider boundary; an all-granted legacy record is safe to discard now.
    clearLegacyStorage();
    legacyDenials = readLegacyDenials();
  }

  // A carried-over opt-out is authoritative from the first read, not only once
  // Cookiebot resolves. Without this the pre-readiness snapshot reported
  // do_not_sell:false for a visitor who had already opted out under the old CMP.
  consentState = Object.freeze({
    ...consentState,
    doNotSell: legacyDenials.doNotSell,
    gpc: legacyDenials.gpc,
  });

  const configuredId = normalizeDomainGroupId(
    domainGroupId ?? readViteEnv('VITE_COOKIEBOT_DOMAIN_GROUP_ID')
  );
  configured = !!configuredId;

  if (!configured) {
    providerUnavailable = true;
    consentState = Object.freeze({ ...consentState, providerConfigured: false });
    console.error(
      '[cookiebot] VITE_COOKIEBOT_DOMAIN_GROUP_ID is missing or invalid; ' +
      'analytics and marketing tags remain disabled. This build is not ready for staging or production.'
    );
    return false;
  }

  attachCookiebotListeners();
  injectCookiebotScript(configuredId);

  // Covers a preloaded script without racing the normal event-first path.
  if (
    window.Cookiebot?.consent &&
    (window.Cookiebot.consent.method !== null || window.Cookiebot.hasResponse === true)
  ) {
    handleCookiebotReady();
  }

  return true;
}

/** Synchronous category check. Pending provider state is denied. */
export function hasConsent(category) {
  return categoryAllowed(category);
}

/**
 * Run now when consent is ready, otherwise queue until Cookiebot resolves.
 * Returns true when executed or queued, false when a ready state denies it.
 */
export function runWhenConsentReady(categories, callback) {
  if (typeof callback !== 'function') return false;
  if (!consentState.ready) {
    if (pendingConsentTasks.length >= MAX_PENDING_CONSENT_TASKS) {
      pendingConsentTasks.shift();
    }
    pendingConsentTasks.push({ categories, callback });
    return true;
  }
  if (!taskCategoriesAllowed(categories)) return false;
  try {
    return callback() !== false;
  } catch {
    return false;
  }
}

export function whenConsentReady() {
  return readyPromise;
}

/**
 * Bounded readiness wait for callers that must snapshot a settled consent state
 * but can never be blocked by the provider — checkout above all. Resolves with
 * the snapshot the moment Cookiebot answers, or with the pending snapshot once
 * the budget elapses. A slow or dead CMP degrades the metadata, it never stalls
 * a purchase.
 */
export function whenConsentSettled(timeoutMs = CONSENT_SETTLE_TIMEOUT_MS) {
  if (consentState.ready || providerUnavailable) {
    return Promise.resolve(getConsentSnapshot());
  }

  const pending = readyPromise;
  return new Promise((resolve) => {
    let timer = null;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      resolve(getConsentSnapshot());
    };

    timer = setTimeout(finish, timeoutMs);
    void pending.then(finish, finish);
  });
}

export function subscribeToConsent(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  listener(getConsentSnapshot());
  return () => listeners.delete(listener);
}

/**
 * Provider lifecycle as three distinct states. `pending` and `unavailable` are
 * NOT consent decisions: nobody has opted out, Cookiebot simply has not spoken
 * yet (or never will on this page load). Collapsing either into a denial is what
 * made every pre-readiness visitor look like an opt-out.
 */
function providerStatus() {
  if (consentState.ready) return 'ready';
  return providerUnavailable ? 'unavailable' : 'pending';
}

/** Existing browser/server conversion eligibility contract. */
export function getConsentSnapshot({ internalTest = false } = {}) {
  const gpc = consentState.gpc || nativeGpcIsActive();
  const doNotSell = consentState.doNotSell || gpc;
  const marketingConsent =
    consentState.ready && consentState.marketing && !doNotSell && !gpc;
  const status = providerStatus();
  let uploadBlockedReason = '';

  // GPC and a carried-over Do Not Sell preference are real user signals and stay
  // authoritative even while the provider is still resolving. Only below them
  // does an unresolved provider get to explain the ineligibility, and only a
  // resolved provider may report a denial.
  if (internalTest) {
    uploadBlockedReason = 'internal_test';
  } else if (gpc) {
    uploadBlockedReason = 'gpc';
  } else if (doNotSell) {
    uploadBlockedReason = 'do_not_sell';
  } else if (status !== 'ready') {
    uploadBlockedReason = `provider_${status}`;
  } else if (!marketingConsent) {
    uploadBlockedReason = 'marketing_consent_denied';
  }

  return {
    ready: consentState.ready,
    provider_status: status,
    provider_configured: consentState.providerConfigured,
    necessary: true,
    analytics: categoryAllowed('analytics'),
    functionality: categoryAllowed('functionality'),
    marketing: marketingConsent,
    marketing_consent: marketingConsent,
    do_not_sell: doNotSell,
    gpc,
    internal_test: !!internalTest,
    ad_upload_eligible: !uploadBlockedReason,
    ...(uploadBlockedReason && { upload_blocked_reason: uploadBlockedReason }),
  };
}

// Test-only module reset. Not imported by application runtime.
export function __resetCookiebotForTests() {
  clearReadinessWatchdog();
  initialized = false;
  configured = false;
  providerUnavailable = false;
  legacyMigrationAttempted = false;
  legacyMigrationFailureReported = false;
  legacyDenials = null;
  listeners.clear();
  pendingConsentTasks.length = 0;
  consentState = Object.freeze({
    ready: false,
    providerConfigured: false,
    necessary: true,
    analytics: false,
    functionality: false,
    marketing: false,
    doNotSell: false,
    gpc: false,
  });
  readyPromise = new Promise((resolve) => {
    readyResolver = resolve;
  });
}
